import React, { useCallback, useRef, useState } from 'react';
import { Body, Button, List, ListItem, Popover } from '@panwds/react-ui';
import { EnvelopeIcon, SignOutIcon, UserIcon } from '@panwds/icons';
import { MENU_ICON_SIZE } from '../utils/iconSizes';

/**
 * The signed-in account menu in the app header: a trigger showing the subscriber's email
 * which opens a two-item menu -- "Edit Subscription" and "Sign Out".
 *
 * Sign Out used to live inside ManageSubscriptionsModal's header. It moved here per UX
 * review: signing out is an account-level action, not a subscription-editing one, so it
 * belongs on the parent page rather than buried in the modal it tears down.
 *
 * Composition notes (why this is PANWDS `Popover` + `List`, not a hand-rolled dropdown):
 *   - `Popover` supplies the portal, popper positioning, `FocusOn` focus trap, and
 *     outside-click/Escape dismissal. `bottom-end` matches the mock's right alignment.
 *   - The trigger uses `Button`'s `isMenu`, which renders the design system's own
 *     caret that auto-rotates from the `isOpen` prop `Popover` injects when it clones the
 *     trigger -- so the mock's caret flip needs no hand-rolled icon swap.
 *   - `List`/`ListItem` is the design system's sanctioned menu primitive (see `List`'s
 *     own JSDoc). Critically, `ListItem` ships the correct hover/active row treatment;
 *     `tailwind.config.js` exposes no row-hover token, so hand-rolling these rows would
 *     mean inventing an `--ssmu-row-hover` across the whole theme pipeline.
 *
 * `showLabel={false}` is load-bearing, not decorative: `Popover` requires a `label` and
 * defaults `showLabel` to `true`, which renders that label as visible text beside the
 * trigger. Setting it false keeps the label in the DOM as the trigger's accessible name
 * (via `aria-labelledby`) while hiding it visually.
 *
 * Accessibility caveat: `Popover` hardcodes `aria-haspopup="dialog"` on the cloned
 * trigger and `role="dialog"` on its card, and `cloneElement` overwrites anything we
 * pass, so we cannot surface `aria-haspopup="menu"`. A dialog wrapping a menu is still
 * valid ARIA, and the roving-focus `role="menu"`/`role="menuitem"` wiring below gives
 * screen-reader and keyboard users the menu semantics that matter.
 */
const SubscriptionMenu = ({ hasValidSession, email, onEdit, onSignOut, onSignIn }) => {
    const [isOpen, setIsOpen] = useState(false);
    const itemRefs = useRef([]);

    const runAndClose = useCallback((action) => {
        setIsOpen(false);
        action?.();
    }, []);

    // Roving focus across the menu items. `Popover`'s FocusOn traps focus inside the
    // panel and handles Escape, so this only needs to cover intra-menu arrow movement
    // plus Enter/Space activation (which `<li>` does not provide natively).
    const handleItemKeyDown = (index, action) => (event) => {
        const items = itemRefs.current.filter(Boolean);
        switch (event.key) {
            case 'Enter':
            case ' ':
                event.preventDefault();
                runAndClose(action);
                break;
            case 'ArrowDown':
                event.preventDefault();
                items[(index + 1) % items.length]?.focus();
                break;
            case 'ArrowUp':
                event.preventDefault();
                items[(index - 1 + items.length) % items.length]?.focus();
                break;
            default:
                break;
        }
    };

    if (!hasValidSession) {
        return (
            <Button onClick={onSignIn} dataTestId="subscription-menu-sign-in">
                Sign In
            </Button>
        );
    }

    const menuItems = [
        {
            key: 'edit',
            label: 'Edit Subscription',
            icon: <EnvelopeIcon size={MENU_ICON_SIZE} aria-hidden="true" className="ssmutw-text-ssmu-icon-secondary" />,
            action: onEdit,
            testId: 'subscription-menu-edit',
        },
        {
            key: 'signout',
            label: 'Sign Out',
            icon: <SignOutIcon size={MENU_ICON_SIZE} aria-hidden="true" className="ssmutw-text-ssmu-icon-secondary" />,
            action: onSignOut,
            testId: 'subscription-menu-sign-out',
        },
    ];

    // `Popover` composes the trigger's accessible name from this label, so it names the
    // account explicitly -- "Account menu" alone would leave screen-reader users unable to
    // tell which account is signed in, even though the email is visible on screen.
    const menuLabel = email ? `Account menu for ${email}` : 'Account menu';

    return (
        <Popover
            label={menuLabel}
            showLabel={false}
            isOpen={isOpen}
            onIsOpenChange={setIsOpen}
            defaultPlacement="bottom-end"
            dataTestId="subscription-menu"
            button={
                <Button isMenu icon={<UserIcon size={MENU_ICON_SIZE} aria-hidden="true" />}>
                    <span className="ssmutw-truncate ssmutw-max-w-[200px]">{email || 'Subscriptions'}</span>
                </Button>
            }
        >
            <div className="ssmutw-min-w-[240px]">
                <div className="ssmutw-px-4 ssmutw-py-2 ssmutw-border-b ssmutw-border-solid ssmutw-border-x-0 ssmutw-border-t-0 ssmutw-border-ssmu-card-border">
                    <Body size="sm" as="div" appearance="secondary">Signed in as</Body>
                    <Body size="sm" as="div" addClassName="ssmutw-font-semibold ssmutw-truncate">
                        {email}
                    </Body>
                </div>

                {/*
                  * `twds-` prefix (not `ssmutw-`) is deliberate: `List` merges this through
                  * twMerge, which only dedupes classes sharing its own prefix. An
                  * `ssmutw-shadow-none` would stack rather than replace, leaving `List`'s
                  * own popover shadow/background double-applied on top of `Popover`'s card.
                  */}
                <List
                    role="menu"
                    aria-label={menuLabel}
                    addClassName="twds-shadow-none twds-bg-transparent twds-backdrop-blur-none"
                    dataTestId="subscription-menu-list"
                >
                    {menuItems.map((item, index) => (
                        <ListItem
                            key={item.key}
                            role="menuitem"
                            tabIndex={0}
                            ref={(node) => { itemRefs.current[index] = node; }}
                            iconLeft={item.icon}
                            onClick={() => runAndClose(item.action)}
                            onKeyDown={handleItemKeyDown(index, item.action)}
                            data-testid={item.testId}
                        >
                            {item.label}
                        </ListItem>
                    ))}
                </List>
            </div>
        </Popover>
    );
};

export default React.memo(SubscriptionMenu);
